import { Banknote, CreditCard, Smartphone } from "lucide-react";
import { Button, Input, Label } from "@/components/ui";
import { MINOR_UNITS_PER_UNIT, formatCurrency } from "@/lib/utils";
import type { PaymentMethod } from "@/types";

interface PaymentMethodSectionProps {
  method: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  amountTendered: string;
  onAmountChange: (value: string) => void;
  referenceNumber: string;
  onReferenceChange: (value: string) => void;
  adjustedTotal: number;
  change: number;
  currencySymbol: string;
}

const QUICK_AMOUNTS = [5, 10, 20, 50, 100];

const TENDER_BUTTONS: { method: PaymentMethod; label: string; icon: typeof Banknote }[] =
  [
    { method: "cash", label: "Cash", icon: Banknote },
    { method: "card", label: "Card", icon: CreditCard },
    { method: "mobile", label: "Mobile", icon: Smartphone },
  ];

export function PaymentMethodSection({
  method,
  onMethodChange,
  amountTendered,
  onAmountChange,
  referenceNumber,
  onReferenceChange,
  adjustedTotal,
  change,
  currencySymbol,
}: PaymentMethodSectionProps) {
  return (
    <>
      <div className="space-y-2">
        <Label>Payment Method</Label>
        <div className="grid grid-cols-3 gap-2">
          {TENDER_BUTTONS.map(({ method: tender, label, icon: Icon }) => (
            <Button
              key={tender}
              variant={method === tender ? "default" : "outline"}
              onClick={() => onMethodChange(tender)}
            >
              <Icon className="mr-2 h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>
      </div>
      {method === "cash" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Amount Tendered</Label>
            <Input
              type="number"
              placeholder="e.g., 20.00"
              value={amountTendered}
              onChange={(e) => onAmountChange(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                variant="outline"
                size="sm"
                onClick={() => onAmountChange(String(amount))}
              >
                {currencySymbol}
                {amount}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onAmountChange(
                  (adjustedTotal / MINOR_UNITS_PER_UNIT).toFixed(2),
                )
              }
            >
              Exact
            </Button>
          </div>
          {change > 0 && (
            <div className="rounded-lg bg-green-50 p-3 text-center">
              <p className="text-sm text-green-600">Change</p>
              <p className="text-xl font-bold text-green-700">
                {formatCurrency(change, currencySymbol)}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Reference Number (Optional)</Label>
          <Input
            placeholder="Transaction reference"
            value={referenceNumber}
            onChange={(e) => onReferenceChange(e.target.value)}
          />
        </div>
      )}
    </>
  );
}
